import SearchableSelect from "components/SearchableSelect/SearchableSelect";
import Spinner from "components/Spinner";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import authHeader from "services/auth-header";
import API from "services/axios";
import { customerLabel } from "./ManualEntryForm";

/**
 * Bulk Update (user, 2026-10-26): link every selected row to one Customer.
 * Not Bulk Update Status (that one asks the couriers).
 *
 * Customer only, no tick boxes (user, 2026-10-26: "Bulk Update should only
 * show customer ... no Checkbox needed"). The customer's location comes with
 * it, as on the form. The API still takes status / courier / service too --
 * the page just does not offer them.
 *
 * ids      the selected rows' ids (show = ids.length > 0 && open)
 * search   text the customer search starts with -- the shipment's company
 *          name (user, 2026-10-30)
 * onDone(message), onHide
 */
export default function BulkUpdateFieldsModal({ show, ids = [], search, onDone, onHide }) {
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setCustomer(null);
    setError("");
  }, [show]);

  const searchCustomers = async (searchText, page) => {
    const { data } = await API.post(
      "/customers",
      { page, query: searchText },
      { headers: authHeader() },
    );
    return {
      results: data?.result || [],
      total: data?.result?.length || 0,
      totalPages: data?.pages || 0,
    };
  };

  const submit = async () => {
    setError("");
    if (!customer) return setError("Choose the customer");
    try {
      setSaving(true);
      const { data } = await API.post(
        "/ups-tracking/bulk-update",
        { ids, fields: { customerId: String(customer.customerId) } },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Update failed");
      onDone?.(data.message);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onHide}>
      <Modal.Body className="shadow-lg bg-white px-3" style={{ borderRadius: 15 }}>
        {saving && <Spinner />}
        <h4 className="mb-1">Update Customer</h4>
        <p className="text-muted small mb-2">
          {ids.length} shipment{ids.length === 1 ? "" : "s"} selected. Each is linked to
          the customer chosen here (and its location).
        </p>
        <hr className="mb-2 mt-1" />

        <div className="row align-items-center mb-2 g-2">
          <label className="col-3 form-label fw-bold mb-0">Customer</label>
          <div className="col-9">
            <SearchableSelect
              searchFunction={searchCustomers}
              defaultSearch={search}
              value={customer}
              selectedLabel={customerLabel(customer)}
              placeholder="Search customer..."
              RenderItem={({ item }) => (
                <div
                  className="px-3 py-1 border-bottom d-flex align-items-center gap-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => setCustomer(item)}
                >
                  <span className="text-muted text-nowrap">C-{item.customerId}</span>
                  <span className="fw-bold w-100 text-nowrap">{item.customerName}</span>
                </div>
              )}
              ResetComponent={() => (
                <div
                  className="px-2 text-danger"
                  style={{ cursor: "pointer" }}
                  onClick={() => setCustomer(null)}
                >
                  Clear
                </div>
              )}
            />
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-1 my-2" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex justify-content-end gap-1 mt-3">
          <button type="button" className="btn btn-secondary" onClick={onHide} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Updating..." : `Update ${ids.length}`}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
